import { isMaskableColumn } from '@/lib/utils/column-masking';
import { DEFAULT_COLUMN_ORDER } from '@/states/column-order.state';
import { ColumnAccessorSortable } from '@/types/columns';

const STORAGE_KEY_PREFIX = 'columnPrefs:';

const VALID_SORTABLE_ACCESSORS = new Set<string>(
  Object.values(ColumnAccessorSortable) as string[]
);

function isValidSortableAccessor(
  value: unknown
): value is ColumnAccessorSortable {
  return typeof value === 'string' && VALID_SORTABLE_ACCESSORS.has(value);
}

export interface ColumnPrefs {
  order: ColumnAccessorSortable[];
  hidden: ColumnAccessorSortable[];
}

function storageKey(dealId: string): string {
  return `${STORAGE_KEY_PREFIX}${dealId}`;
}

/**
 * Merges saved order with default order so that any column that exists in the
 * app default but was not in the saved snapshot (e.g. new column or column
 * that was business-hidden when saved) is appended at the end.
 */
function mergeOrderWithDefault(
  savedOrder: ColumnAccessorSortable[]
): ColumnAccessorSortable[] {
  const savedSet = new Set(savedOrder);
  const appended = DEFAULT_COLUMN_ORDER.filter((col) => !savedSet.has(col));
  return [...savedOrder, ...appended];
}

/**
 * Filters hidden list to only valid sortable accessors; new columns (not in
 * saved hidden) are considered visible.
 */
function sanitizeHidden(raw: unknown): ColumnAccessorSortable[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isValidSortableAccessor).filter(isMaskableColumn);
}

/**
 * Sanitizes order: only valid sortable accessors, then merge with default
 * so new columns are appended.
 */
function sanitizeOrder(raw: unknown): ColumnAccessorSortable[] {
  if (!Array.isArray(raw)) return [...DEFAULT_COLUMN_ORDER];
  const valid = raw.filter(isValidSortableAccessor);
  return mergeOrderWithDefault(valid);
}

/**
 * Loads column preferences for a deal from localStorage.
 * Returns null if nothing saved or invalid; otherwise merged order and hidden.
 * New columns (present in app default but not in saved data) are appended to
 * order and considered visible (not in hidden).
 */
export function loadColumnPrefs(dealId: string): ColumnPrefs | null {
  if (!dealId) return null;
  try {
    const raw = localStorage.getItem(storageKey(dealId));
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (parsed === null || typeof parsed !== 'object') return null;
    const order = sanitizeOrder((parsed as { order?: unknown }).order);
    const hidden = sanitizeHidden((parsed as { hidden?: unknown }).hidden);
    return { order, hidden };
  } catch {
    return null;
  }
}

/**
 * Saves column order and hidden list for a deal.
 * Only saves when dealId is non-empty.
 */
export function saveColumnPrefs(
  dealId: string,
  order: ColumnAccessorSortable[],
  hidden: ColumnAccessorSortable[]
): void {
  if (!dealId) return;
  try {
    localStorage.setItem(
      storageKey(dealId),
      JSON.stringify({ order: [...order], hidden: [...hidden] })
    );
  } catch {
    // ignore quota or other storage errors
  }
}
